import {
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'

import { theme, useHealthToolsCache } from '@hhgtech/hhg-components'
import {
  LOCALE_HAS_COMMUNITY_SITE,
  QUERY_HEALTH_TOOL,
} from '@hhgtech/hhg-components/constants'
import { DatePicker, Input, Text } from '@hhgtech/hhg-components/mantine'
import { Flex, Image, Tooltip } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'

import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import { DueDateInfoType } from 'components/pages/healthTools/due-date'
import { useAppContext } from 'hooks/useAppContext'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { API_DATE_FORMAT, assetImagePath } from 'interfaces/constants'
import { HEALTH_TOOL_TYPE } from 'interfaces/constants/healthTools'

import { FormControl, StyledLastPeriodFormContainer } from './index.styled'
import {
  DueDateFormValues,
  DueDateCalculatorType,
  getDueDateUltrasound,
} from './utils'

type Props = {
  onSubmit?: (info: DueDateInfoType, redirectLink?: string) => void
  isEmbed?: boolean
  controlledSubmit?: boolean
  form: UseFormReturnType<
    DueDateFormValues,
    (values: DueDateFormValues) => DueDateFormValues
  >
  scrollIntoExplaination?: () => void
}

export type HandlerProps = {
  submitForm: any
}

const DueDateFormUltrasound: ForwardRefRenderFunction<HandlerProps, Props> = (
  { onSubmit, isEmbed = false, controlledSubmit, form, scrollIntoExplaination },
  ref,
) => {
  const { formatMessage: f } = useIntl()
  const {
    state: { isLoggedIn },
  } = useAppContext()
  const isMobile = useIsMobile()
  const { locale, router } = useRouterExtend()
  const { getHealthToolLink } = useHealthToolsCache(locale)

  const linkTool = useMemo(() => getHealthToolLink(HEALTH_TOOL_TYPE.DUE_DATE), [
    getHealthToolLink,
  ])

  const [dateOfUltrasound, setDateOfUltrasound] = useState(new Date())
  const inputPropsCRL = form.getInputProps('ultrasoundCRL')
  const inputPropsMSD = form.getInputProps('ultrasoundMSD')

  useImperativeHandle(ref, () => ({
    submitForm: onSubmitForm,
  }))

  const onSubmitForm = async () => {
    const currentUrl = isEmbed
      ? `${window.location.origin}/${linkTool}`
      : window.location.origin + window.location.pathname

    const additionalInfo = `${inputPropsCRL.value}|${inputPropsMSD.value}`
    const params = [
      `${QUERY_HEALTH_TOOL.type}=${DueDateCalculatorType.ultrasound}`,
      `${QUERY_HEALTH_TOOL.startDate}=${dayjs(dateOfUltrasound).format(
        API_DATE_FORMAT,
      )}`,
      `${QUERY_HEALTH_TOOL.additional}=${additionalInfo}`,
      LOCALE_HAS_COMMUNITY_SITE.includes(locale) ? 'joincommunity=1' : '',
    ]
      .filter(Boolean)
      .join('&')
    const redirectLink = currentUrl + `?${params}`

    if (controlledSubmit) {
      const info = await getDueDateUltrasound(dateOfUltrasound, additionalInfo)
      onSubmit && onSubmit(info, redirectLink)
      return
    }

    if (!isLoggedIn) {
      redirectToSSOWithReturnUrl(redirectLink, 'due-date')
    }
    if (isEmbed) {
      router.push(redirectLink)
    } else {
      const info = await getDueDateUltrasound(dateOfUltrasound, additionalInfo)
      onSubmit && onSubmit(info, redirectLink)
    }
  }

  const rightSection = (
    <Text size="p4" color={theme.colors.gray400}>
      mm
    </Text>
  )

  return (
    <StyledLastPeriodFormContainer>
      <FormControl>
        <DatePicker
          label={
            <Text as="label" size="s4">
              {f({
                id: 'dueDatePage.date_of_ultrasound',
                defaultMessage: 'Date of Ultrasound',
              })}{' '}
            </Text>
          }
          value={dateOfUltrasound}
          onChange={(value) => {
            setDateOfUltrasound(value || new Date())
          }}
          excludeDate={(date) =>
            dayjs(date).isBefore(dayjs().subtract(43, 'week'))
          }
          size="md"
        />
      </FormControl>
      <Flex align="center" gap={4} mb={4}>
        <Text as="label" size="s4">
          {f({
            id: 'dueDatePage.validation.one_is_required',
            defaultMessage: 'At least one is required',
          })}
        </Text>
        <Tooltip
          withinPortal
          withArrow
          width={300}
          multiline
          label={f({ id: 'dueDatePage.ultrasound.tooltip' })}
        >
          <Image
            width={18}
            height={18}
            src={`${assetImagePath}/info-icon.svg`}
            alt=""
            onClick={scrollIntoExplaination}
          />
        </Tooltip>
      </Flex>
      <FormControl>
        <Flex gap={isMobile ? 8 : 12}>
          <Input
            placeholder="CRL"
            sx={{ flex: 1 }}
            rightSection={rightSection}
            type="number"
            min={0}
            step={0.01}
            {...inputPropsCRL}
            errorProps={{ sx: { display: 'none' } }}
          />
          <Input
            placeholder="MSD"
            sx={{ flex: 1 }}
            rightSection={rightSection}
            type="number"
            min={0}
            step={0.01}
            {...inputPropsMSD}
            errorProps={{ sx: { display: 'none' } }}
          />
        </Flex>
        {inputPropsCRL.error || inputPropsMSD.error ? (
          <Text size="p5" color={theme.colors.red700}>
            {inputPropsCRL.error || inputPropsMSD.error}
          </Text>
        ) : null}
      </FormControl>
    </StyledLastPeriodFormContainer>
  )
}

export default forwardRef(DueDateFormUltrasound)
